import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Container from '@cloudscape-design/components/container';
import Header from '@cloudscape-design/components/header';
import SpaceBetween from '@cloudscape-design/components/space-between';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Box from '@cloudscape-design/components/box';
import Link from '@cloudscape-design/components/link';
import Button from '@cloudscape-design/components/button';
import Alert from '@cloudscape-design/components/alert';
import { Ticket } from '../types/ticket';
import { TicketStatusBadge } from './TicketStatusBadge';
import { SeverityBadge } from './SeverityBadge';
import ExpandableSection from '@cloudscape-design/components/expandable-section';
import { Markdown } from './Markdown';
import { sourceLinkLabel } from '../utils/sourceLink';
import { resummarizeTicket, getQueueStatus, getTicket } from '../api/tickets';

interface Props {
  ticket: Ticket;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <Box variant="awsui-key-label">{label}</Box>
      <div>{children}</div>
    </div>
  );
}

export function TicketDetails({ ticket }: Props) {
  const navigate = useNavigate();
  const [resummarizing, setResummarizing] = useState(false);
  const [newSummary, setNewSummary] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, []);

  const handleResummarize = async () => {
    setResummarizing(true);
    setError(null);
    setNewSummary(null);
    try {
      await resummarizeTicket(ticket.ticketId);
      // Poll queue until empty, then re-fetch ticket
      pollRef.current = setInterval(async () => {
        try {
          const status = await getQueueStatus();
          const remaining = status.queue.messagesAvailable + status.queue.messagesInFlight;
          if (remaining === 0) {
            if (pollRef.current) clearInterval(pollRef.current);
            const updated = await getTicket(ticket.ticketId);
            setNewSummary(updated.ticket.aiSummary);
            setResummarizing(false);
          }
        } catch {
          // keep polling
        }
      }, 3000);
    } catch (e: any) {
      setError(`Re-summarize failed: ${e.message}`);
      setResummarizing(false);
    }
  };

  const displaySummary = newSummary ?? ticket.aiSummary;

  return (
    <SpaceBetween size="l">
      <Container header={<Header variant="h2">Overview</Header>}>
        <ColumnLayout columns={3} variant="text-grid">
          <Field label="Ticket ID">{ticket.ticketId}</Field>
          <Field label="Status"><TicketStatusBadge status={ticket.status} /></Field>
          <Field label="Severity">{ticket.severity ? <SeverityBadge severity={ticket.severity} /> : '—'}</Field>
          <Field label="Assignee">
            {ticket.assignee
              ? /^[a-z]{2,12}$/.test(ticket.assignee)
                ? <Link href={`https://phonetool.amazon.com/users/${ticket.assignee}`} external>{ticket.assignee}</Link>
                : ticket.assignee
              : 'Unassigned'}
          </Field>
          <Field label="Requester">{ticket.requester || '—'}</Field>
          <Field label="Submitter">{ticket.submitter || '—'}</Field>
          <Field label="Created">{ticket.createdAt ? new Date(ticket.createdAt).toLocaleString() : '—'}</Field>
          <Field label="Updated">{ticket.updatedAt ? new Date(ticket.updatedAt).toLocaleString() : '—'}</Field>
          <Field label="Source">
            {ticket.sourceTicketUrl ? (
              <Link href={ticket.sourceTicketUrl} external>
                {sourceLinkLabel(ticket.sourceTicketUrl)}
              </Link>
            ) : '—'}
          </Field>
        </ColumnLayout>
      </Container>

      <ExpandableSection headerText="Description" defaultExpanded={false}>
        <Markdown content={ticket.description || 'No description available.'} />
      </ExpandableSection>

      <Container header={
        <Header variant="h2" actions={
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
            <Button onClick={handleResummarize} loading={resummarizing} iconName="gen-ai">
              Re-summarize
            </Button>
            <Link variant="secondary" fontSize="body-s" onFollow={(e) => { e.preventDefault(); navigate('/settings'); }}>Edit AI config in Settings</Link>
          </div>
        }>
          AI Summary
        </Header>
      }>
        {error && <Alert type="error" dismissible onDismiss={() => setError(null)}>{error}</Alert>}
        {newSummary && <Alert type="success" dismissible onDismiss={() => setNewSummary(null)}>Summary updated with current AI config.</Alert>}
        <Markdown content={displaySummary || 'No summary available.'} />
      </Container>

      {((ticket.tags?.length > 0) || (ticket.labels?.length > 0)) && (
        <Container header={<Header variant="h2">Tags & Labels</Header>}>
          <ColumnLayout columns={2} variant="text-grid">
            <Field label="Tags">{ticket.tags?.join(', ') || '—'}</Field>
            <Field label="Labels">{ticket.labels?.join(', ') || '—'}</Field>
          </ColumnLayout>
        </Container>
      )}
    </SpaceBetween>
  );
}
